import { Component, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { forkJoin, of } from 'rxjs';
import { ApiService } from '../core/api.service';
import { ToastService } from '../core/toast.service';
import { ResumeTemplate, SectionDef, blankContent, templateFor } from './resume-templates';

@Component({
  selector: 'app-resume-editor',
  templateUrl: './resume-editor.component.html'
})
export class ResumeEditorComponent implements OnInit {
  service: any;
  template: ResumeTemplate;
  resumeId: number = null;
  title = '';
  styleName = 'classic';
  status = 'DRAFT';
  content: any = {};
  open: { [k: string]: boolean } = {};
  tagInput: { [k: string]: string } = {};
  busy = false;
  dirty = false;
  tab: 'edit' | 'preview' = 'edit';

  constructor(private api: ApiService, private route: ActivatedRoute, private router: Router, private toast: ToastService) {}

  ngOnInit() {
    const slug = this.route.snapshot.paramMap.get('slug');
    const id = this.route.snapshot.paramMap.get('id');
    const isNew = id === 'new';

    forkJoin(
      this.api.get('/resumes/services/' + slug),
      isNew ? this.api.get('/profile') : of(null),
      isNew ? of(null) : this.api.get('/resumes/' + id)
    ).subscribe(([svc, profile, resume]: any[]) => {
      this.service = svc.service;
      this.template = templateFor(this.service.serviceCode);
      if (resume) {
        this.resumeId = resume.resumeId;
        this.title = resume.resumeTitle;
        this.styleName = resume.templateStyle;
        this.status = resume.status;
        this.content = Object.assign(blankContent(this.template, null), resume.content || {});
      } else {
        this.title = (profile && profile.firstName ? profile.firstName + ' — ' : '') + this.service.title.replace(/^Take a Free /, '');
        this.content = blankContent(this.template, profile);
      }
      this.template.sections.forEach((s, i) => this.open[s.key] = i < 2);
    }, e => { this.toast.err(ApiService.message(e)); this.router.navigate(['/my-resumes', slug]); });
  }

  // ---------- list sections
  addItem(s: SectionDef) {
    const item: any = {};
    s.fields.forEach(f => item[f.key] = '');
    this.content[s.key].push(item);
    this.touch();
  }
  removeItem(s: SectionDef, i: number) { this.content[s.key].splice(i, 1); this.touch(); }
  move(s: SectionDef, i: number, dir: number) {
    const arr = this.content[s.key];
    const j = i + dir;
    if (j < 0 || j >= arr.length) { return; }
    [arr[i], arr[j]] = [arr[j], arr[i]];
    this.touch();
  }

  // ---------- tag sections
  addTag(s: SectionDef, ev?: Event) {
    if (ev) { ev.preventDefault(); }
    const raw = (this.tagInput[s.key] || '').trim();
    if (!raw) { return; }
    raw.split(',').map(x => x.trim()).filter(Boolean).forEach(t => {
      if (this.content[s.key].indexOf(t) < 0) { this.content[s.key].push(t); }
    });
    this.tagInput[s.key] = '';
    this.touch();
  }
  removeTag(s: SectionDef, i: number) { this.content[s.key].splice(i, 1); this.touch(); }

  touch() { this.dirty = true; }

  save(markFinal = false) {
    if (!this.title.trim()) { this.toast.err('Give the résumé a title.'); return; }
    if (markFinal) { this.status = 'FINAL'; }
    const body = { serviceId: this.service.serviceId, resumeTitle: this.title, templateStyle: this.styleName, status: this.status, content: this.content };
    this.busy = true;
    const call = this.resumeId ? this.api.put('/resumes/' + this.resumeId, body) : this.api.post('/resumes', body);
    call.subscribe(r => {
      this.busy = false;
      this.dirty = false;
      this.toast.ok(r.message);
      if (!this.resumeId && r.resumeId) {
        this.resumeId = r.resumeId;
        this.router.navigate(['/my-resumes', this.service.slug, r.resumeId], { replaceUrl: true });
      }
    }, e => { this.busy = false; this.toast.err(ApiService.message(e)); });
  }

  print() { window.print(); }

  back() {
    if (this.dirty && !confirm('You have unsaved changes. Leave anyway?')) { return; }
    this.router.navigate(['/my-resumes', this.service.slug]);
  }

  trackIdx(i: number) { return i; }
}
